import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SurveyQuestionRenderer from "./SurveyQuestionRenderer";
import { convertToResponseObject } from "./surveyUtils";
import { useSubmitPollResponse } from "./api/pollsV2";

/**
 * Example usage of SurveyQuestionRenderer
 * This component demonstrates how to use the renderer with the example questions
 * @param {Object} poll - Can be either:
 *   1. Full poll object with { id, content: { questions: [] } }
 *   2. Array of questions directly
 * @param {number|string} pollId - Optional poll ID if poll is just an array of questions
 */
const SurveyQuestionRendererExample = ({ poll, pollId }) => {
  const { t } = useTranslation(["survey"]);
  const history = useHistory();
  const { submitResponse } = useSubmitPollResponse();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [surveyResponses, setSurveyResponses] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Handle both poll formats: full object or questions array
  const questions = Array.isArray(poll)
    ? poll
    : (poll && poll.content && poll.content.questions) || [];
  const currentQuestion = questions[currentQuestionIndex];

  // Filter out informational-only questions (welcome_screen, end_screen, terms_and_conditions) for step counting
  const isInformationalQuestion = question =>
    question.type === "welcome_screen" ||
    question.type === "end_screen" ||
    question.type === "terms_and_conditions";

  // Calculate total steps excluding informational questions
  const totalSteps = questions.filter(q => !isInformationalQuestion(q)).length;

  // Calculate current step number excluding informational questions before current index
  const currentStep = questions
    .slice(0, currentQuestionIndex + 1)
    .filter(q => !isInformationalQuestion(q)).length;

  const handleSubmit = async values => {
    // Save the current question's responses
    const updatedResponses = {
      ...surveyResponses,
      ...values,
    };
    setSurveyResponses(updatedResponses);

    // Move to next question or complete survey
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      if (!isSubmitted) {
        // If survey not yet submitted (e.g., no terms_and_conditions), submit now
        setIsSubmitting(true);
        setSubmitError(null);

        try {
          const responseData = convertToResponseObject(updatedResponses);

          // Submit the response to the API
          await submitResponse({
            poll_id: pollId,
            content: responseData,
          });

          setIsSubmitted(true);
          history.push("/");
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Failed to submit survey:", error);
          setSubmitError(t("survey:error.submit"));
          return; // Do not navigate away if submission failed
        } finally {
          setIsSubmitting(false);
        }
      }

      history.push("/");
    }

    // Check if this is the terms_and_conditions step - mark survey as completed
    if (currentQuestion.type === "terms_and_conditions") {
      // Submit the survey responses
      setIsSubmitting(true);
      setSubmitError(null);

      try {
        const responseData = convertToResponseObject(updatedResponses);

        // Submit the response to the API
        await submitResponse({
          poll_id: pollId,
          content: responseData,
        });

        setIsSubmitted(true);

        // Move to next question (end screen)
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        // Or redirect to thank you page
        // history.push("/surveys/thank-you");
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to submit survey:", error);
        setSubmitError(t("survey:error.submit"));
        // Still move to next question to show end screen
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSkip = () => {
    // Skip current question and move to next
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  return (
    <div className="survey-container">
      {submitError && (
        <div
          className="survey-error"
          style={{
            padding: "1rem",
            marginBottom: "1rem",
            backgroundColor: "#f8d7da",
            color: "#721c24",
            borderRadius: "4px",
            border: "1px solid #f5c6cb",
          }}
        >
          {submitError}
        </div>
      )}
      <SurveyQuestionRenderer
        question={currentQuestion}
        onSubmit={handleSubmit}
        onPrevious={handlePrevious}
        onSkip={handleSkip}
        currentStep={currentStep}
        totalSteps={totalSteps}
        isFirst={currentQuestionIndex === 0}
        isLast={currentQuestionIndex === questions.length - 1}
        initialValues={surveyResponses}
        disabled={isSubmitting}
      />
      {isSubmitting && (
        <div
          className="survey-submitting"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "2rem",
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            <p>{t("survey:wizard.sending")}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyQuestionRendererExample;
